/**
 * Búsqueda tolerante a errores ortográficos para el directorio.
 *
 * - Normaliza acentos y mayúsculas (NFD).
 * - Acepta coincidencias por subcadena O por distancia de Levenshtein.
 * - Mapa de alias: palabras comunes que la gente escribe mal (franses,
 *   francez, engles…) se canonizan antes de comparar.
 * - Multi-palabra: TODOS los tokens del query deben encontrar match
 *   (búsqueda tipo AND, como espera el usuario).
 *
 * Pensado para datasets pequeños (≤ 200 entries) que se filtran en cliente.
 * El costo de Levenshtein se acota saltando comparaciones cuando la
 * diferencia de longitudes ya supera el umbral.
 */

export function stripAccents(s) {
  return (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * Devuelve los tokens normalizados a buscar (post-alias) para una query.
 * Útil para resaltar coincidencias en la UI sin reimplementar la lógica.
 */
export function expandQueryTokens(query) {
  const q = stripAccents(query).trim();
  if (!q) return [];
  const seen = new Set();
  const out = [];
  for (const tok of q.split(/\s+/).filter(Boolean)) {
    for (const variant of [tok, ALIASES[tok]]) {
      if (variant && !seen.has(variant)) {
        seen.add(variant);
        out.push(variant);
      }
    }
  }
  return out;
}

// Palabras que la gente comúnmente escribe mal — canonizamos antes de
// comparar. El destino es la forma SIN acentos (porque la búsqueda ya
// normaliza). Tokens cortos compartidos entre idioma y otra cosa se evitan.
const ALIASES = {
  // Francés
  franses: 'frances', francez: 'frances', france: 'frances', francais: 'frances', french: 'frances',
  // Inglés
  english: 'ingles', engles: 'ingles', inles: 'ingles', ingless: 'ingles', ingels: 'ingles',
  // Alemán
  aleman: 'aleman', alleman: 'aleman', deutsch: 'aleman', german: 'aleman', alemen: 'aleman',
  // Italiano
  italian: 'italiano', italiani: 'italiano', italianno: 'italiano',
  // Portugués
  portuguese: 'portugues', portugez: 'portugues', portuges: 'portugues', portugais: 'portugues',
  // Japonés
  japanese: 'japones', japonez: 'japones', japon: 'japones',
  // Chino / Mandarín
  chines: 'chino', chinise: 'chino', mandarin: 'chino', mandaarin: 'chino', chinese: 'chino',
  // Ruso
  russian: 'ruso', russo: 'ruso', rrusso: 'ruso',
  // Árabe
  arab: 'arabe', arabic: 'arabe', arabigo: 'arabe', arabe: 'arabe',
  // Hebreo
  hebrew: 'hebreo', hebreu: 'hebreo',
  // Coreano
  korean: 'coreano', koreano: 'coreano', coreana: 'coreano',
  // Holandés / Neerlandés
  dutch: 'holandes', neerlandes: 'holandes', holandez: 'holandes',
  // Sueco / Noruego / Danés
  swedish: 'sueco', norwegian: 'noruego', danish: 'danes',
  // Polaco / Turco / Griego / Catalán
  polish: 'polaco', turkish: 'turco', greek: 'griego', catalan: 'catalan',
  // Especialidades comunes
  medico: 'medico', medical: 'medico', medicina: 'medico',
  juridico: 'juridico', legal: 'juridico', judicial: 'juridico',
  finanziero: 'financiero', financial: 'financiero', finanzas: 'financiero',
  tecnologico: 'tecnologia', tech: 'tecnologia', tecnico: 'tecnico', technical: 'tecnico',
  conferencia: 'conferencia', conference: 'conferencia',
  simultaneo: 'simultanea', simultaneous: 'simultanea', simultanea: 'simultanea',
  consecutivo: 'consecutiva', consecutive: 'consecutiva',
};

function canonize(token) {
  return ALIASES[token] || token;
}

function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev = new Array(b.length + 1);
  const curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}

/** Umbral de tolerancia según longitud de la palabra buscada. */
function distanceThreshold(len) {
  if (len <= 3) return 0;   // 1-3 chars: exact only
  if (len <= 5) return 1;   // 4-5 chars: 1 typo
  if (len <= 8) return 2;   // 6-8 chars: 2 typos
  return 3;                 // 9+ chars: 3 typos
}

/**
 * ¿Algún token del haystack hace match con `queryToken`?
 *
 * Estrategia: subcadena directa → subcadena con alias → Levenshtein con
 * alias. Cortocircuita al primer match.
 */
function tokenMatchesAny(haystackTokens, queryToken) {
  const canonical = canonize(queryToken);
  const threshold = distanceThreshold(canonical.length);

  // 1) Subcadena rápida — cubre el 95% de los casos.
  for (const t of haystackTokens) {
    if (t.includes(queryToken) || t.includes(canonical)) return true;
  }
  if (threshold === 0) return false;

  // 2) Levenshtein por palabra (saltando si la longitud difiere mucho).
  for (const t of haystackTokens) {
    if (Math.abs(t.length - canonical.length) > threshold) continue;
    if (levenshtein(t, canonical) <= threshold) return true;
  }
  return false;
}

/**
 * Devuelve true si la búsqueda `query` hace match con `haystackText`.
 * Multi-token: cada palabra del query debe encontrar match en algún
 * token del haystack (AND).
 */
export function fuzzyContains(haystackText, query) {
  const q = stripAccents(query).trim();
  if (!q) return true;
  const qTokens = q.split(/\s+/).filter(Boolean);
  if (qTokens.length === 0) return true;
  const hay = stripAccents(haystackText);
  const hayTokens = hay.split(/[\s,;.()/|-]+/).filter(Boolean);
  // Para queries de 1 token, también permitimos match contra el haystack
  // completo (cadena unida) — así "puebla" sigue matcheando "Puebla, MX"
  // aún si tokenizamos por comas.
  return qTokens.every((qt) => tokenMatchesAny(hayTokens, qt) || hay.includes(canonize(qt)));
}
